import { FlexRow } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { OnChangeParams, Select } from 'baseui/select';
import React, { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useStyles } from '../../hooks/useStyles';
import { jobsStatusState } from '../../storage/jobStates';

type Props = {
  selectedJobStatusIds?: number[];
  onChangeJobStatusIds?: (jobStatusIds?: number[]) => void;
  isFilter?: boolean;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  error?: React.ReactNode;
  onBlur?: () => void;
  inline?: boolean;
  clearable?: boolean;
  required?: boolean;
  maxDropdownHeight?: string;
  smallContainer?: boolean;
  dropdownOffset?: string;
};
export function JobStatusMultiSelect({
  selectedJobStatusIds,
  onChangeJobStatusIds,
  isFilter = true,
  label = 'Estado:',
  emptyOptionText = 'Seleciona un estado',
  showEmptyOption = false,
  error,
  onBlur,
  inline,
  clearable = false,
  required = false,
  maxDropdownHeight,
  smallContainer,
  dropdownOffset,
}: Readonly<Props>) {
  const { theme, css } = useStyles();
  const jobStatusList = useRecoilValue(jobsStatusState);
  const [selectedJobStatusOptions, setSelectedJobStatusOptions] = useState<{ id: string; label: string }[]>();

  const jobStatusOptions = useMemo(() => {
    if (!jobStatusList) return;
    const coreJobStatusOptions = jobStatusList.map((jobStatus) => ({ id: jobStatus.value, label: jobStatus.name }));
    const jobStatusOptions = showEmptyOption
      ? [
          {
            id: undefined,
            label: isFilter ? 'Todos' : emptyOptionText,
          },
          ...coreJobStatusOptions,
        ]
      : coreJobStatusOptions;
    if (selectedJobStatusOptions) {
      selectedJobStatusOptions.forEach((jobStatusOption) => {
        if (!jobStatusOptions.find((c) => c.id === jobStatusOption.id)) jobStatusOptions.push(jobStatusOption);
      });
    }
    return jobStatusOptions;
  }, [jobStatusList]);

  const onJobStatusChange = (params: OnChangeParams) => {
    if (params.option?.id !== undefined) {
      const selectedOption = params.option.id as number;
      const newStatusOptions = selectedJobStatusIds?.includes(selectedOption)
        ? selectedJobStatusIds.filter((id) => id !== selectedOption)
        : Array.from(new Set([...(selectedJobStatusIds || []), selectedOption]));
      onChangeJobStatusIds && onChangeJobStatusIds(newStatusOptions);
    } else {
      onChangeJobStatusIds && onChangeJobStatusIds(undefined);
    }
    setSelectedJobStatusOptions(
      params.value.map((val) => val).filter((val) => val.id !== undefined) as { id: string; label: string }[]
    );
  };

  const dropdownMarginTop = dropdownOffset ? dropdownOffset : theme.sizing.scale1200;

  return (
    <Select
      options={jobStatusOptions}
      value={
        showEmptyOption || selectedJobStatusIds !== undefined
          ? selectedJobStatusIds?.map((status) => ({ id: `${status}` }))
          : undefined
      }
      multi
      onChange={onJobStatusChange}
      closeOnSelect={false}
      filterOutSelected={false}
      maxDropdownHeight={maxDropdownHeight}
      clearable={clearable}
      size="compact"
      error={!!error}
      onBlur={onBlur}
      overrides={{
        Root: {
          style: {
            width: '100%',
            maxWidth: '250px',
          },
        },
        ValueContainer: {
          component: inline
            ? ({ children }) => {
                return (
                  <FlexRow gap={theme.sizing.scale100}>
                    <span
                      className={css({
                        paddingLeft: theme.sizing.scale400,
                        paddingTop: theme.sizing.scale200,
                        paddingBottom: theme.sizing.scale200,
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                      })}
                    >
                      {label ? `${label} ${required ? '*' : ''}` : undefined}
                    </span>
                    <FlexRow
                      classNames={css({
                        paddingTop: theme.sizing.scale200,
                        paddingBottom: theme.sizing.scale200,
                        gap: theme.sizing.scale0,
                        whiteSpace: 'nowrap',
                      })}
                    >
                      {children[1]}
                      {selectedJobStatusIds && selectedJobStatusIds?.length > 0 ? (
                        <span
                          className={css({
                            fontSize: theme.typography.ParagraphSmall.fontSize,
                            color: theme.colors.contentPrimary,
                          })}
                        >
                          {selectedJobStatusIds?.length}{' '}
                          {selectedJobStatusIds?.length > 1 ? 'seleccionados' : 'seleccionado'}
                        </span>
                      ) : (
                        'Selecciona un estado'
                      )}
                    </FlexRow>
                  </FlexRow>
                );
              }
            : undefined,
        },
        Dropdown: {
          style: () => ({
            marginTop: smallContainer ? dropdownMarginTop : 0,
          }),
        },
        Popover: {
          props: {
            placement: 'bottom',
            overrides: {
              Body: {
                style: () => ({
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                }),
              },
              Inner: {
                style: () => ({
                  backgroundColor: 'transparent',
                }),
              },
            },
          },
        },
        OptionContent: {
          component: React.forwardRef<HTMLLIElement, any>((props, ref) => {
            const { children, $selected, $isHighlighted, ...restProps } = props;

            return (
              <li
                ref={ref}
                {...restProps}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: $isHighlighted ? '#eef3f8' : undefined,
                }}
              >
                <Checkbox
                  checked={$selected}
                  overrides={{
                    Label: () => null,
                  }}
                />
                <span style={{ marginLeft: 8 }}>{children}</span>
              </li>
            );
          }),
        },
      }}
    />
    // <EnhancedLabeledSelect
    //   label={label}
    //   options={jobStatusOptions}
    //   value={showEmptyOption || selectedJobStatusId !== undefined ? [{ id: selectedJobStatusId }] : undefined}
    //   onChange={onJobStatusChange}
    //   fullWidth
    //   onBlur={() => {
    //     onBlur && onBlur();
    //   }}
    //   searchable
    //   inline={inline}
    //   inset={inline}
    //   error={error}
    //   required={required}
    //   clearable={clearable}
    //   placeholder={emptyOptionText}
    //   // onAdd={onAddClick}
    //   maxDropdownHeight={maxDropdownHeight}
    //   smallContainer={smallContainer}
    //   dropdownOffset={dropdownOffset}
    // />
  );
}
